import { groupBy } from "lodash";
import React from "react";
import Link from "next/link";
import {
  isValidIconName,
  OakFlex,
  OakGrid,
  OakGridArea,
  OakHeading,
  OakHoverLink,
  OakSubjectIconButton,
} from "@oaknational/oak-components";

import type { Subject } from "@/types/ui";

type Props = {
  subjects: Subject[];
  optionsUrlTemplate: string;
  unitsUrlTemplate: string;
  onSubjectSelected?: (subject: Subject) => void;
};
export const SubjectsPageView = ({
  subjects,
  optionsUrlTemplate,
  unitsUrlTemplate,
  onSubjectSelected,
}: Props) => {
  const groupedBySubject = groupBy(
    subjects,
    (subject: Subject) => subject.programmeFields.subjectSlug,
  );

  const nonCurriculum = subjects.filter(
    (subject) => subject.features?.nonCurriculum,
  );

  const orderedKeys = Object.keys(groupedBySubject).sort((a, b) =>
    a.localeCompare(b),
  );

  const buildSubjectGridArea = ({
    subjectSlug,
    showNonCurriculum = false,
  }: {
    subjectSlug: string;
    showNonCurriculum: boolean;
  }) => {
    const subjectData = groupedBySubject[subjectSlug];
    const features = subjectData?.[0]?.features;

    if (features?.nonCurriculum !== true && showNonCurriculum) {
      return null;
    }
    if (features?.nonCurriculum === true && !showNonCurriculum) {
      return null;
    }

    if (subjectData?.[0]?.programmeFields.phaseSlug === "foundation") {
      throw new Error("Foundation phase is not supported");
    }
    if (!subjectData?.[0]) {
      throw new Error(`Error no subject data available for ${subjectSlug}`);
    }

    const examOptions = groupBy(
      subjectData,
      (subject: Subject) => subject.programmeFields.examboardSlug,
    );
    const tierOptions = groupBy(
      subjectData,
      (subject: Subject) => subject.programmeFields.tierSlug,
    );
    const pathwayOptions = groupBy(
      subjectData,
      (subject: Subject) => subject.programmeFields.pathwaySlug,
    );

    const hasOptions =
      Object.keys(examOptions).length > 1 ||
      Object.keys(tierOptions).length > 1 ||
      Object.keys(pathwayOptions).length > 1;

    // If there are multiple matches on subjectSlug, show the non-legacy one.
    const cycle1Subject = subjectData.find(
      (subject: Subject) => !subject.isLegacy,
    );
    const subject = cycle1Subject || subjectData[0];

    const programmeSlug = hasOptions ? subject.baseSlug : subject.programmeSlug;
    const url = hasOptions
      ? optionsUrlTemplate.replace(":programmeSlug", programmeSlug)
      : unitsUrlTemplate.replace(":programmeSlug", programmeSlug);

    const iconSlug = `subject-${subject.programmeFields.subjectSlug}`;
    return (
      <OakGridArea $colSpan={1} key={subjectSlug} role="listitem">
        <OakFlex $height={"100%"}>
          <OakSubjectIconButton
            variant={"vertical"}
            $textAlign={"center"}
            key={subjectSlug}
            element={Link}
            prefetch={true}
            subjectIconName={
              isValidIconName(iconSlug) ? iconSlug : "question-mark"
            }
            href={url}
            phase={
              showNonCurriculum
                ? "non-curriculum"
                : (subject.programmeFields.phaseSlug as "primary" | "secondary")
            }
            onClick={() => {
              onSubjectSelected?.(subject);
            }}
          >
            {subject.programmeFields.subject}
          </OakSubjectIconButton>
        </OakFlex>
      </OakGridArea>
    );
  };

  return (
    <OakFlex
      $flexDirection="column"
      $background={"mint"}
      $alignItems={"center"}
      $justifyContent={"flex-start"}
      $minHeight={"100vh"}
    >
      <OakFlex
        $alignSelf={"flex-start"}
        $mt={"spacing-32"}
        $ml={"spacing-56"}
        $mb={"spacing-16"}
      >
        <OakHoverLink
          href={`/classroom/browse`}
          iconName="chevron-left"
          element={Link}
        >
          Back
        </OakHoverLink>
      </OakFlex>
      <OakFlex $flexDirection="column" $mb={"spacing-32"}>
        <OakFlex
          $background={"bg-primary"}
          $pv={["spacing-16", "spacing-32", "spacing-48"]}
          $ph={["spacing-16", "spacing-32", "spacing-48"]}
          $borderRadius={"border-radius-l"}
          $ba={"border-solid-s"}
          $borderColor={"border-decorative1-stronger"}
          $flexDirection={"column"}
          $alignItems={"center"}
        >
          <OakHeading
            $font={["heading-6", "heading-5"]}
            tag="h1"
            $textAlign={"left"}
            $mr={"auto"}
          >
            Choose a subject
          </OakHeading>
          <OakGrid
            $mt={"spacing-24"}
            $pb={"spacing-24"}
            $gridTemplateColumns={[
              "repeat(2, 1fr)",
              "repeat(4, 1fr)",
              "repeat(5, 1fr)",
            ]}
            $rg={"spacing-16"}
            $cg={"spacing-16"}
            role="list"
          >
            {orderedKeys.map((subjectSlug) =>
              buildSubjectGridArea({ subjectSlug, showNonCurriculum: false }),
            )}
          </OakGrid>
          {nonCurriculum.length > 0 && (
            <>
              <OakHeading
                $font={["heading-6", "heading-5"]}
                tag="h2"
                $mr={"auto"}
              >
                Further lessons
              </OakHeading>
              <OakGrid
                $mt={"spacing-24"}
                $pb={"spacing-24"}
                $gridTemplateColumns={[
                  "repeat(2, 1fr)",
                  "repeat(4, 1fr)",
                  "repeat(5, 1fr)",
                ]}
                $rg={"spacing-16"}
                $cg={"spacing-16"}
                role="list"
              >
                {orderedKeys.map((subjectSlug) =>
                  buildSubjectGridArea({
                    subjectSlug,
                    showNonCurriculum: true,
                  }),
                )}
              </OakGrid>
            </>
          )}
        </OakFlex>
      </OakFlex>
    </OakFlex>
  );
};
